import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const EmailVerification = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState('verifying'); // verifying, verified, expired, invalid
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [settingPassword, setSettingPassword] = useState(false);

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.post(`${API}/auth/verify-email/${token}`);
      if (response.data.message === 'Email verified successfully') {
        setVerificationStatus('verified');
      }
    } catch (error) {
      if (error.response?.status === 400) {
        const detail = error.response.data.detail;
        if (detail === 'Token expired') {
          setVerificationStatus('expired');
        } else {
          setVerificationStatus('invalid');
        }
      } else {
        setVerificationStatus('invalid');
      }
    }
  };

  const handlePasswordSetup = async (e) => {
    e.preventDefault();
    setSettingPassword(true);

    // Validate passwords
    if (password.length < 8) {
      toast({
        title: 'Password Too Short',
        description: 'Password must be at least 8 characters long',
        variant: 'destructive',
      });
      setSettingPassword(false);
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: 'Passwords Do Not Match',
        description: 'Please make sure both passwords match',
        variant: 'destructive',
      });
      setSettingPassword(false);
      return;
    }

    try {
      await axios.post(`${API}/auth/setup-password`, {
        token: token,
        password: password
      });

      toast({
        title: 'Password Set Successfully!',
        description: 'You can now log in with your email and password.',
      });

      // Redirect to profile completion or home
      setTimeout(() => {
        navigate('/profile');
      }, 2000);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to set password. Please try again.';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setSettingPassword(false);
    }
  };

  if (verificationStatus === 'verifying') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700 mx-auto mb-4"></div>
            <p className="text-gray-700">Verifying your email...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (verificationStatus === 'expired') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex justify-center mb-4">
              <AlertCircle className="h-12 w-12 text-orange-600" />
            </div>
            <CardTitle className="text-center text-gray-900">Verification Link Expired</CardTitle>
            <CardDescription className="text-center">
              This verification link has expired. Please request a new one.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => navigate('/register')} 
              className="w-full bg-red-700 hover:bg-red-900 text-white"
            >
              Return to Registration
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (verificationStatus === 'invalid') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex justify-center mb-4">
              <AlertCircle className="h-12 w-12 text-red-600" />
            </div>
            <CardTitle className="text-center text-gray-900">Invalid Verification Link</CardTitle>
            <CardDescription className="text-center">
              This verification link is invalid. Please check your email for the correct link.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => navigate('/register')} 
              className="w-full bg-red-700 hover:bg-red-900 text-white"
            >
              Return to Registration
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex justify-center mb-4">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>
          <CardTitle className="text-center text-gray-900">Email Verified!</CardTitle>
          <CardDescription className="text-center">
            Your email has been verified. Please set up your password to complete your registration.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordSetup} className="space-y-4">
            <div>
              <Label htmlFor="password" className="text-gray-900">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    className="border-gray-300"
                    minLength={8}
                    autoComplete="new-password"
                  />
              <p className="text-sm text-gray-600 mt-1">Must be at least 8 characters</p>
            </div>
            <div>
              <Label htmlFor="confirm-password" className="text-gray-900">Confirm Password *</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    required
                    className="border-gray-300"
                    minLength={8}
                    autoComplete="new-password"
                  />
            </div>
            <Button
              type="submit"
              className="w-full bg-red-700 hover:bg-red-900 text-white"
              disabled={settingPassword}
            >
              {settingPassword ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Setting Password...
                </>
              ) : (
                <>
                  <Lock className="mr-2 h-4 w-4" />
                  Set Password & Complete Registration
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailVerification;

